import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import {
  QueueWithAssignmentCounts,
  ReadQueuesApiArg,
  ReadQueuesApiResponse,
} from 'lib-shared-types-api';
import { useEffect, useMemo, useRef } from 'react';
import { useUpdateEffect } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { queuesApi } from 'apis/queues';
import {
  useLazyPaginatedEndpoint,
  UsePaginatedResult,
} from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

import { useDashboardLandingPage } from '../../../utils/useDashboardLandingPage';

import { useQueueDashboardParamsToApiArg } from './useQueueDashboardParamsToApiArg';

type UseQueueDashboardQueuesResult = UsePaginatedResult<
  QueueWithAssignmentCounts,
  ReadQueuesApiArg,
  ReadQueuesApiResponse
>;

/**
 * A hook that handles fetching and updating dashboard queues based on URL
 * params and other conditions.
 */
export const useQueueDashboardQueues = (): UseQueueDashboardQueuesResult => {
  const storage = useServerStorage('user');
  const { user } = useCurrentUser();
  const { params, setParams, replaceParams } =
    useAppSearchParams('/dashboard/queues');

  const hasAnalyticsAccess = useHasAccess('analytics');
  const { resetLandingPage } = useDashboardLandingPage();

  // Controls whether we should skip calling `changeArg` on the paginated endpoint, so we don't
  // trigger re-fetches when we shouldn't, such as in response to the arg changing after pagination.
  const skipChangeArg = useRef(false);

  const { queueParamsToApiArg, queueSortStringToObject } =
    useQueueDashboardParamsToApiArg();

  const result = useLazyPaginatedEndpoint({
    endpoint: queuesApi.endpoints.readQueues,
    itemsSelector: ({ data: { queues } }) => queues,
    totalSelector: ({ data: { queues, number_of_queues } }) =>
      number_of_queues ?? queues.length,
  });

  const [readFavorite, { isFetching: isFetchingFavorite }] =
    dashboardApi.useLazyReadDashboardFavoriteQuery();

  // Handles changes to the URL search params, and updates the endpoint arg accordingly, triggering
  // a refetch in most cases, except in the case of "favorites", which is handled separately below.
  useEffect(() => {
    if (skipChangeArg.current) {
      skipChangeArg.current = false;
    } else {
      const arg = queueParamsToApiArg();

      result.changeArg(arg, {
        skipFetch: params.type === 'favourite',
      });
    }
    // We only care when params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Handles initializing favorites, both the old favorites from user-storage and the new
  // dashboard favorites identified with an ID in the URL params.
  useEffect(() => {
    if (params.type === 'favourite') {
      // If we have a specific favorite ID, we need to fetch that favorite first to get the queue IDs.
      if (params.id) {
        readFavorite(
          { id: params.id },
          true, // return cached data if available
        )
          .unwrap()
          .then((response) => {
            const { favorite } = response.data;
            const { queue_ids = [], sort_by } = favorite.params;

            if (queue_ids.length) {
              const arg = {
                ...queueParamsToApiArg(),
                queueIds: queue_ids,
                // If there is a sort_by param in the URL, it takes precedence over anything else.
                ...(!params.sort_by &&
                  // If no sort_by in URL, use favorite's sort if any, otherwise undefined, which
                  // results in the queues being returned in the order of the queue_ids array.
                  {
                    sort: sort_by
                      ? queueSortStringToObject(sort_by)
                      : undefined,
                  }),
              };

              result.changeArg(arg);
            }

            ampli.dashboardFavoriteViewed({
              shared: favorite.shared,
              isOwner: favorite.user.id === user.id,
            });
          })
          // Failed to fetch the favorite (e.g. it doesn't exist or user doesn't have access).
          .catch(() => {
            const { landingPage } = storage.data.dashboard ?? {};

            // If the user's landing page was set to the invalid favorite, reset it to avoid
            // navigating to it again in the future.
            if (landingPage?.url?.path === '/dashboard/queues') {
              const { type, id } = landingPage.url.params.search ?? {};

              if (type === 'favourite' && id === params.id) {
                resetLandingPage();
              }
            }

            // Redirect to the default view.
            replaceParams({ type: hasAnalyticsAccess ? 'all' : 'agent' });
          });
      } else {
        // If no specific favorite ID is set, fall back to using the old favorites from user-storage.
        // @TODO: Remove this logic when we fully migrate to dashboard favorites.
        const { favoriteQueueIds = [] } = storage.data.dashboard ?? {};

        if (favoriteQueueIds.length) {
          result.changeArg({
            ...queueParamsToApiArg(),
            queueIds: favoriteQueueIds,
          });
        }
      }
    }
    // We only care when params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Handles updating the URL search params whenever the arg limit and offset changes.
  useUpdateEffect(() => {
    const arg = queueParamsToApiArg();

    if (result.arg?.limit !== arg.limit || result.arg?.offset !== arg.offset) {
      setParams({
        limit: result.arg?.limit?.toString(),
        offset: result.arg?.offset?.toString(),
      });

      // We don't want to re-fetch through changeArg to avoid recursive-ness.
      skipChangeArg.current = true;
    }

    // Only run this when the pagination args change.
  }, [result.arg?.limit, result.arg?.offset]);

  return <UseQueueDashboardQueuesResult>useMemo(() => {
    return {
      ...result,

      ...(params.type === 'favourite' && {
        // Show the loading state if we're fetching the selected favorite.
        asyncStatus: isFetchingFavorite ? 'pending' : result.asyncStatus,

        // Show the empty state immediately if the favorite has no queues.
        // This is necessary because the queues paginated endpoint is not called
        // when there are no queue IDs, so we need to override the state here.
        ...(!isFetchingFavorite &&
          !result.arg?.queueIds?.length && {
            asyncStatus: 'fulfilled',
            items: [],
          }),
      }),
    };
  }, [isFetchingFavorite, params.type, result]);
};
